<script>
import appConfig from '@/app.config'
import moment from 'moment'
import { Helper, debounce } from '@/mixin/helper'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import { getSmsClient } from '@/services/sms'
import Multiselect from 'vue-multiselect'

import { workOrderTypesOdoo } from '../lib/workOrderTypesOdoo'

moment.locale('id')

const vsmsSmsAxios = getSmsClient()

export default {
  page: {
    title: 'Done',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
    Multiselect,
  },

  mixins: [Helper],

  data() {
    return {
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      totalPages: 0,
      pageOptions: [10, 25, 50, 100],
      sortDesc: true,
      filter: null,
      filteredItems: [],
      sortBy: 'wo_number',
      loading: false,
      isLoading: false,
      workOrderList: [],
      show_model: null,
      woTypes: [],
    }
  },

  mounted() {
    this.getWorkOrderList()
  },

  computed: {
    title() {
      return this.$t('workOrder.column.done')
    },

    rows() {
      return this.workOrderList.length
    },

    visibleFields() {
      if (this.show_model && this.show_model.length > 0) {
        let result = this.show_model.map((a) => a.key)
        return this.workOrder.filter(function (item) {
          return item.key == 'check' || result.includes(item.key)
        })
      } else {
        return this.workOrder
      }
    },

    workOrder() {
      return [
        {
          key: 'wo_number',
          label: this.$t('workOrder.table.woNumber'),
          sortable: true,
        },
        {
          key: 'company_name',
          label: this.$t('workOrder.table.customer'),
        },
        {
          key: 'user_name',
          label: this.$t('workOrder.table.technician'),
        },
        {
          key: 'planned_at',
          label: this.$t('workOrder.table.processingDate'),
          sortable: true,
        },
        {
          key: 'actual_installation',
          label: this.$t('workOrder.table.actualInstall'),
          sortable: true,
        },
        {
          key: 'wo_type',
          label: this.$t('workOrder.table.woType'),
          formatter: (value) => {
            const mapping = workOrderTypesOdoo().find((map) => map.code === value)
            return mapping ? mapping.name : value
          },
        },
        {
          key: 'version',
          label: this.$t('workOrder.table.version'),
        },
      ]
    },

    woTypeOptions() {
      const types = workOrderTypesOdoo()
      return types.map((type) => {
        const codeMapping = {
          install: 'I',
          maintenance: 'M',
          move: 'P'
        }
        return {
          ...type,
          code: codeMapping[type.code] || type.code,
        }
      })
    },
  },

  methods: {
    async getWorkOrderList() {
      this.loading = true
      let qp = {
        filters: 'woStatus:eq:D',
        page: this.currentPage,
        size: this.perPage,
        q: this.filter,
      }

      if (this.woTypes.length > 0) {
        let typeCodes = this.woTypes.map((type) => type.code)
        if (typeCodes.includes('I')) {
          typeCodes.push('A')
        }
        const woTypeFilters = `woType:in:${typeCodes.join('|')}`
        qp.filters = `${qp.filters};${woTypeFilters}`
      } else {
        let typeCodes = this.woTypeOptions.map((type) => type.code)
        if (typeCodes.includes('I')) {
          typeCodes.push('A')
        }
        const woTypeFilters = `woType:in:${typeCodes.join('|')}`
        qp.filters = `${qp.filters};${woTypeFilters}`
      }

      let sortDirection = this.sortDesc ? 'desc' : 'asc'
      let sortColumn
      switch (this.sortBy) {
        case 'started_at':
          sortColumn = 'startedOn'
          break
        case 'actual_installation':
          sortColumn = 'actualInstallation'
          break
        default:
          sortColumn = 'woNumber'
          break
      }
      qp.sorts = `${sortColumn}:${sortDirection}`

      const workOrderList = await vsmsSmsAxios
        .get(`work-order/v2`, {
          params: qp,
        })
        .catch((error) => {
          this.handleError(error)
        })
      if (!workOrderList?.data?.data) return
      this.workOrderList = workOrderList.data.data
      this.filteredItems = workOrderList.data.data
      this.totalRows = workOrderList.data.total_count
      this.currentPage = workOrderList.data.page
      this.perPage = workOrderList.data.size
      this.totalPages = workOrderList.data.total_pages
      this.loading = false
    },

    handleError(error) {
      if (error.response) {
        if (error.response.status === 400) {
          if (error.response.data.details) {
            error.response.data.details.forEach((element) => {
              this.$toast.error(
                `${error.response.data.error} : ${element.field} ${element.message}`
              )
            })
          }
        } else {
          this.$toast.error(error.response.data.error)
        }
      } else if (error.request) {
        this.$toast.error(error.response.statusText)
      } else {
        this.$toast.error(error.message)
      }
    },

    changePage(e) {
      this.currentPage = e
      this.getWorkOrderList()
    },

    resetFilter(e) {
      this.currentPage = 1
      this.size = e
      this.getWorkOrderList()
    },

    searchData: debounce(function () {
      this.getWorkOrderList()
    }, 600),

    generateLink(item) {
      return `/work-order/validation/form/${item.wo_number}`
    },

    sortingChanged(ctx) {
      this.sortDesc = ctx.sortDesc
      this.sortBy = ctx.sortBy

      this.getWorkOrderList()
    },

    onSelectedWoType() {
      this.getWorkOrderList()
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="clearfix mc-btn-group__list">
      <b-button
        @click="$router.push({ name: 'Work Order' })"
        variant="outline-primary"
        class="ms-3"
      >
        <i class="bx bx-arrow-back"></i> {{ $t('global.back') }}
      </b-button>
    </div>
    <div class="card mc-card mc-card-front">
      <div class="card-body">
        <b-tabs content-class="p-3 text-muted">
          <b-tab active class="border-0">
            <template v-slot:title>
              <span class="d-inline-block d-sm-none">
                <i class="fas fa-home"></i>
              </span>
              <span class="d-none d-sm-inline-block">{{ $t('workOrder.column.done') }}</span>
            </template>
            <template>
              <div>
                <div class="row">
                  <div class="col-12">
                    <div class="card">
                      <div class="card-body">
                        <div class="clearfix">
                          <div class="float-start">
                            <multiselect
                              v-model="woTypes"
                              :options="woTypeOptions"
                              :multiple="true"
                              :close-on-select="false"
                              :clear-on-select="false"
                              :preserve-search="true"
                              :hide-selected="true"
                              :allow-empty="true"
                              placeholder="Select work order types"
                              label="name"
                              track-by="code"
                              class="mb-2"
                              @input="onSelectedWoType"
                              style="min-width: 202px;"
                            />
                          </div>
                          <div class="float-end">
                            <label class="d-inline-flex align-items-center mc-table-view__search">
                              <i class="mc-fa fas fa-search"></i>
                              <b-form-input
                                v-model="filter"
                                type="search"
                                :placeholder="`${$t('global.search')} ${$t(
                                  'workOrder.table.woNumber'
                                )}...`"
                                class="ms-2"
                                @input="searchData"
                              ></b-form-input>
                            </label>
                          </div>
                        </div>

                        <div class="table-responsive my-2 border border-1">
                          <b-table
                            small
                            show-empty
                            :items="workOrderList"
                            :fields="visibleFields"
                            class="datatables"
                            empty-table
                            :empty-text="$t('global.emptyTable')"
                            :empty-filtered-text="$t('global.emptyFilteredTable')"
                            :sort-by.sync="sortBy"
                            :sort-desc.sync="sortDesc"
                            @sort-changed="sortingChanged"
                            :busy="loading"
                          >
                            <template #table-busy>
                              <div class="text-center text-danger my-2">
                                <b-spinner class="align-middle"></b-spinner>
                              </div>
                            </template>
                            <template #cell(wo_number)="data">
                              <strong>
                                <a :href="generateLink(data.item)" class="text-primary">
                                  {{ data.item.wo_number }}
                                </a>
                              </strong>
                            </template>
                            <template #cell(planned_at)="data">
                              {{
                                data.item.planned_at
                                  ? data.item.version
                                    ? momentDMHms(data.item.planned_at)
                                    : momentDMHmsUtc(data.item.planned_at)
                                  : '-'
                              }}
                            </template>
                            <template #cell(version)="data">
                              {{ data.item.version ? data.item.version : '-' }}
                            </template>
                          </b-table>
                        </div>

                        <div class="row">
                          <div class="col-sm-12 col-md-6">
                            <div id="tickets-table_length" class="dataTables_length">
                              <label class="d-inline-flex align-items-center">
                                <span>Menampilkan</span>
                                <span>
                                  <b-form-select
                                    class="form-select form-select-sm"
                                    v-model="perPage"
                                    size="sm"
                                    :options="pageOptions"
                                    @change="resetFilter"
                                  />
                                </span>
                                <span>entri.&nbsp;&nbsp;</span>
                                <span>
                                  Menampilkan&nbsp;
                                  <strong>
                                    {{
                                      filteredItems.length > 0 ? (currentPage - 1) * perPage + 1 : 0
                                    }}
                                  </strong>
                                  &nbsp;sampai&nbsp;
                                  <strong>
                                    {{
                                      currentPage != totalPages
                                        ? currentPage * perPage
                                        : (currentPage - 1) * perPage + filteredItems.length
                                    }}
                                  </strong>
                                  &nbsp;dari&nbsp;
                                  <strong>{{ totalRows }}</strong>
                                  &nbsp;entri.
                                </span>
                              </label>
                            </div>
                          </div>
                          <div class="col-sm-12 col-md-6">
                            <div class="dataTables_paginate paging_simple_numbers float-end">
                              <ul class="pagination pagination-rounded mb-0">
                                <!-- pagination -->
                                <b-pagination
                                  v-model="currentPage"
                                  :total-rows="totalRows"
                                  :per-page="perPage"
                                  @change="changePage"
                                ></b-pagination>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </b-tab>
        </b-tabs>
      </div>
    </div>
  </Layout>
</template>
